import React, { Suspense, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
  DragOverlay,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { MetricCard } from "./MetricCard";
import { NewsCard } from './NewsCard';
import { LeaksCard } from './LeaksCard';
import { EOLCard } from './EOLCard';
import { ReleasesCard } from './ReleasesCard';
import { VulnerabilitiesCard } from './VulnerabilitiesCard';
import { RansomwareCard } from './RansomwareCard';
import { SortableCard } from './SortableCard';
import { useLanguage } from "@/hooks/useLanguage";
import { Lock, Database, Globe, AlertOctagon, AlertCircle, AlertTriangle, TrendingUp } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { DashboardSettings, CardSizes, CardSize, getItemCountFromHeight } from "@/hooks/useDashboardSettings";
import { cn } from "@/lib/utils";

// Lazy load heavy components
const RansomwareMapWidget = React.lazy(() => import('./RansomwareMapWidget').then(module => ({ default: module.RansomwareMapWidget })));

interface DashboardCardsProps {
  data: any;
  isLoading: boolean;
  days: number;
  settings: DashboardSettings;
  onLayoutChange?: (newOrder: string[]) => void;
  onCardSizeChange?: (cardId: keyof CardSizes, size: Partial<CardSize>) => void;
}

// Mapping from card IDs to cardSizes keys
const CARD_SIZE_MAP: Record<string, keyof CardSizes | null> = {
  mapWidget: 'mapWidget',
  vulnerabilitiesCard: 'vulnerabilitiesCard',
  highVulnerabilitiesCard: 'highVulnerabilitiesCard',   // New
  mediumVulnerabilitiesCard: 'mediumVulnerabilitiesCard', // New
  lowVulnerabilitiesCard: 'lowVulnerabilitiesCard',       // New
  newsCard: 'newsCard',
  ransomwareCard: 'ransomwareCard',
  leaksCard: 'leaksCard',
  eolCard: 'eolCard',
  releasesCard: 'releasesCard',
};

export function DashboardCards({ data, isLoading, days, settings, onLayoutChange, onCardSizeChange }: DashboardCardsProps) {
  const { t } = useLanguage();
  const timeLabel = t('dashboard.last_days').replace('{days}', days.toString());
  const [activeId, setActiveId] = useState<string | null>(null);

  // Configure sensors for drag detection
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Filter ransomware attacks based on settings
  const filteredRansomwareAttacks = useMemo(() => {
    const attacks = data?.recentRansomwareAttacks || [];
    if (settings.ransomwareFilters.countries.length === 0 && settings.ransomwareFilters.groups.length === 0) {
      return attacks;
    }
    return attacks.filter((attack: any) => {
      const countryMatch = settings.ransomwareFilters.countries.length === 0 ||
        settings.ransomwareFilters.countries.includes(attack.country);
      const groupMatch = settings.ransomwareFilters.groups.length === 0 ||
        settings.ransomwareFilters.groups.includes(attack.group);
      return countryMatch && groupMatch;
    });
  }, [data?.recentRansomwareAttacks, settings.ransomwareFilters]);

  // Filter news based on settings
  const filteredNews = useMemo(() => {
    const news = data?.latestNews || [];
    if (settings.newsSources.length === 0) {
      return news;
    }
    if (settings.newsSources.includes('__NONE__')) {
      return [];
    }
    return news.filter((item: any) => settings.newsSources.includes(item.source));
  }, [data?.latestNews, settings.newsSources]);

  // Filter releases based on settings
  const filteredReleases = useMemo(() => {
    const releases = data?.latestReleases || [];
    if (settings.releaseProducts.length === 0) {
      return releases;
    }
    if (settings.releaseProducts.includes('__NONE__')) {
      return [];
    }
    return releases.filter((release: any) => settings.releaseProducts.includes(release.nazwa_projektu));
  }, [data?.latestReleases, settings.releaseProducts]);

  // Filter EOL items based on settings
  const filteredEOL = useMemo(() => {
    const items = data?.upcomingEOL || [];
    if (settings.eolProducts.length === 0) {
      return items;
    }
    if (settings.eolProducts.includes('__NONE__')) {
      return [];
    }
    return items.filter((item: any) => settings.eolProducts.includes(item.name));
  }, [data?.upcomingEOL, settings.eolProducts]);

  // Count visible metrics in top row
  const visibleTopMetrics = [
    settings.metrics.ransomwareVictims,
    settings.metrics.dataLeaks,
    settings.metrics.activeGroup,
    settings.metrics.mostAttackedCountry
  ].filter(Boolean).length;

  // Count visible CVE metrics
  const visibleCVEMetrics = [
    settings.metrics.criticalCVE,
    settings.metrics.highCVE,
    settings.metrics.mediumCVE,
    settings.metrics.lowCVE
  ].filter(Boolean).length;

  // Dynamic grid classes based on visible items
  const getGridClass = (count: number) => {
    if (count === 1) return 'grid-cols-1';
    if (count === 2) return 'grid-cols-1 md:grid-cols-2';
    if (count === 3) return 'grid-cols-1 md:grid-cols-3';
    return 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4';
  };

  // Get card size for a card ID
  const getCardSize = (cardId: string): CardSize => {
    const sizeKey = CARD_SIZE_MAP[cardId];
    if (sizeKey && settings.cardSizes[sizeKey]) {
      return settings.cardSizes[sizeKey];
    }
    // Default fallback
    return { width: 1, height: 1 };
  };

  // Get item count based on card height
  const getItemCount = (cardId: string): number => {
    const size = getCardSize(cardId);
    return getItemCountFromHeight(size.height);
  };

  const renderCardContent = (cardType: string) => {
    if (!settings[cardType as keyof DashboardSettings]) return null;

    const itemCount = getItemCount(cardType);

    switch (cardType) {
      case 'mapWidget':
        return (
          <div className="h-full w-full">
            <Suspense fallback={<Skeleton className="w-full h-full rounded-xl" />}>
              <RansomwareMapWidget data={data?.allRansomwareAttacks || []} isLoading={isLoading} days={days} />
            </Suspense>
          </div>
        );
      case 'vulnerabilitiesCard':
        return (
          <div className="h-full">
            <VulnerabilitiesCard
              severity="critical"
              vulnerabilities={(data?.recentCriticalVulnerabilities || []).slice(0, itemCount)}
              isLoading={isLoading}
            />
          </div>
        );
      case 'highVulnerabilitiesCard':
        return (
          <div className="h-full">
            <VulnerabilitiesCard
              severity="high"
              vulnerabilities={(data?.recentHighVulnerabilities || []).slice(0, itemCount)}
              isLoading={isLoading}
            />
          </div>
        );
      case 'mediumVulnerabilitiesCard':
        return (
          <div className="h-full">
            <VulnerabilitiesCard
              severity="medium"
              vulnerabilities={(data?.recentMediumVulnerabilities || []).slice(0, itemCount)}
              isLoading={isLoading}
            />
          </div>
        );
      case 'lowVulnerabilitiesCard':
        return (
          <div className="h-full">
            <VulnerabilitiesCard
              severity="low"
              vulnerabilities={(data?.recentLowVulnerabilities || []).slice(0, itemCount)}
              isLoading={isLoading}
            />
          </div>
        );
      case 'newsCard':
        return (
          <div className="h-full">
            <NewsCard
              news={filteredNews.slice(0, itemCount)}
              isLoading={isLoading}
            />
          </div>
        );
      case 'ransomwareCard':
        return (
          <div className="h-full">
            <RansomwareCard
              ransomwareAttacks={filteredRansomwareAttacks.slice(0, itemCount)}
              isLoading={isLoading}
            />
          </div>
        );
      case 'leaksCard':
        return (
          <div className="h-full">
            <LeaksCard
              leaks={(data?.recentLeaks || []).slice(0, itemCount)}
              isLoading={isLoading}
            />
          </div>
        );
      case 'eolCard':
        return (
          <div className="h-full">
            <EOLCard
              eolItems={filteredEOL.slice(0, itemCount)}
              isLoading={isLoading}
            />
          </div>
        );
      case 'releasesCard':
        return (
          <div className="h-full">
            <ReleasesCard
              releases={filteredReleases.slice(0, itemCount)}
              isLoading={isLoading}
            />
          </div>
        );
      default:
        return null;
    }
  };

  // Get only visible cards from layout order
  const visibleCards = useMemo(() => {
    return settings.layoutOrder.filter(id => settings[id as keyof DashboardSettings]);
  }, [settings.layoutOrder, settings]);

  // Handle drag start
  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  // Handle drag end
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);

    if (over && active.id !== over.id) {
      const oldIndex = settings.layoutOrder.indexOf(active.id as string);
      const newIndex = settings.layoutOrder.indexOf(over.id as string);

      if (oldIndex !== -1 && newIndex !== -1) {
        const newOrder = arrayMove(settings.layoutOrder, oldIndex, newIndex);
        onLayoutChange?.(newOrder);
      }
    }
  };

  // Handle card resize
  const handleCardResize = (cardId: string, size: Partial<CardSize>) => {
    const sizeKey = CARD_SIZE_MAP[cardId];
    if (sizeKey && onCardSizeChange) {
      onCardSizeChange(sizeKey, size);
    }
  };

  const activeLayout = settings.layoutOrder.filter(id => settings[id as keyof DashboardSettings]);

  if (activeLayout.length === 0 && !settings.metricsRow && !settings.cveMetrics) {
    return (
      <div className="flex flex-col items-center justify-center p-12 text-center text-muted-foreground">
        <p className="text-lg mb-2">{t('common.no_results')}</p>
        <p className="text-sm">Enable cards in settings to see dashboard content.</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Top Metrics Row */}
      {settings.metricsRow && visibleTopMetrics > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className={cn("grid gap-4 mb-6", getGridClass(visibleTopMetrics))}
        >
          {settings.metrics.ransomwareVictims && (
            <div className="h-full">
              <MetricCard
                icon={<Lock className="h-6 w-6" />}
                title={t('dashboard.ransomware_victims')}
                value={isLoading ? undefined : data?.ransomwareVictimsCount}
                description={timeLabel}
                gradient="bg-gradient-to-br from-cyan-500/20 to-blue-500/20 hover:from-cyan-500/30 hover:to-blue-500/30 border-cyan-500/50"
                textColor="text-cyan-500"
                linkTo="/ransomware"
              />
            </div>
          )}
          {settings.metrics.dataLeaks && (
            <div className="h-full">
              <MetricCard
                icon={<Database className="h-6 w-6" />}
                title={t('dashboard.data_leaks')}
                value={isLoading ? undefined : data?.leaksCount}
                description={timeLabel}
                gradient="bg-gradient-to-br from-indigo-500/20 via-purple-500/20 to-pink-500/20 hover:from-indigo-500/30 hover:via-purple-500/30 hover:to-pink-500/30 border-purple-500/50"
                textColor="text-purple-500"
                linkTo="/leaks"
              />
            </div>
          )}
          {settings.metrics.activeGroup && (
            <div className="h-full">
              <MetricCard
                icon={<Lock className="h-6 w-6" />}
                title={t('dashboard.active_group')}
                value={isLoading ? undefined : data?.mostActiveRansomwareGroup}
                description={timeLabel}
                gradient="bg-gradient-to-br from-rose-500/20 to-orange-500/20 hover:from-rose-500/30 hover:to-orange-500/30 border-rose-500/50"
                textColor="text-rose-500"
                isText={true}
                linkTo={data?.mostActiveRansomwareGroup ? `/ransomware/group/${data.mostActiveRansomwareGroup}` : "/ransomware"}
              />
            </div>
          )}
          {settings.metrics.mostAttackedCountry && (
            <div className="h-full">
              <MetricCard
                icon={<Globe className="h-6 w-6" />}
                title={t('dashboard.most_attacked_country')}
                value={isLoading ? undefined : data?.mostAttackedCountry}
                description={timeLabel}
                gradient="bg-gradient-to-br from-pink-500/20 to-red-500/20 hover:from-pink-500/30 hover:to-red-500/30 border-pink-500/50"
                textColor="text-pink-500"
                isText={true}
                linkTo={data?.mostAttackedCountry ? `/ransomware/${data.mostAttackedCountry.toLowerCase()}` : "/ransomware"}
              />
            </div>
          )}
        </motion.div>
      )}

      {/* Vulnerability Metrics Row */}
      {settings.cveMetrics && visibleCVEMetrics > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className={cn("grid gap-4 mb-6", getGridClass(visibleCVEMetrics))}
        >
          {settings.metrics.criticalCVE && (
            <div>
              <MetricCard
                icon={<AlertOctagon className="h-5 w-5" />}
                title={t('dashboard.critical_vulnerabilities')}
                value={isLoading ? undefined : data?.criticalVulnerabilitiesCount}
                description={timeLabel}
                gradient="bg-gradient-to-br from-red-600/20 to-red-800/20 hover:from-red-600/30 hover:to-red-800/30 border-red-600/50"
                textColor="text-red-500"
                linkTo="/cve"
              />
            </div>
          )}
          {settings.metrics.highCVE && (
            <div>
              <MetricCard
                icon={<AlertCircle className="h-5 w-5" />}
                title={t('dashboard.high_vulnerabilities')}
                value={isLoading ? undefined : data?.highVulnerabilitiesCount}
                description={timeLabel}
                gradient="bg-gradient-to-br from-orange-500/20 to-amber-600/20 hover:from-orange-500/30 hover:to-amber-600/30 border-orange-500/50"
                textColor="text-orange-500"
                linkTo="/cve"
              />
            </div>
          )}
          {settings.metrics.mediumCVE && (
            <div>
              <MetricCard
                icon={<AlertTriangle className="h-5 w-5" />}
                title={t('dashboard.medium_vulnerabilities')}
                value={isLoading ? undefined : data?.mediumVulnerabilitiesCount}
                description={timeLabel}
                gradient="bg-gradient-to-br from-yellow-400/20 to-amber-500/20 hover:from-yellow-400/30 hover:to-amber-500/30 border-yellow-400/50"
                textColor="text-yellow-500"
                linkTo="/cve"
              />
            </div>
          )}
          {settings.metrics.lowCVE && (
            <div>
              <MetricCard
                icon={<TrendingUp className="h-5 w-5" />}
                title={t('dashboard.low_vulnerabilities')}
                value={isLoading ? undefined : data?.lowVulnerabilitiesCount}
                description={timeLabel}
                gradient="bg-gradient-to-br from-emerald-400/20 to-teal-500/20 hover:from-emerald-400/30 hover:to-teal-500/30 border-emerald-400/50"
                textColor="text-emerald-500"
                linkTo="/cve"
              />
            </div>
          )}
        </motion.div>
      )}

      {/* Main Draggable Grid with dnd-kit */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={visibleCards} strategy={rectSortingStrategy}>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 auto-rows-max grid-flow-dense">
            {visibleCards.map((cardId) => {
              const content = renderCardContent(cardId);
              if (!content) return null;

              const cardSize = getCardSize(cardId);
              const isResizable = true;

              return (
                <SortableCard
                  key={cardId}
                  id={cardId}
                  cardSize={cardSize}
                  onResize={(size) => handleCardResize(cardId, size)}
                  isResizable={isResizable}
                >
                  {content}
                </SortableCard>
              );
            })}
          </div>
        </SortableContext>

        {/* Drag Overlay for visual feedback */}
        <DragOverlay>
          {activeId ? (
            <div className="opacity-90 shadow-2xl rounded-xl ring-2 ring-primary">
              {renderCardContent(activeId)}
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>
    </div>
  );
}
